import {
  WorkActivity,
  WorkActivityAcAllocationItem,
  WorkActivityBankAllocationItem
} from '@/services/types/work-activity.types';
import { getCurrencySymbol } from '@/utils/budgetCalculations';

const AC_ALLOCATION_FIELD_NAMES = [
  'department_accounting_center',
  'product_and_services_accounting_center',
  'project_accounting_center',
  'contract_accounting_center'
] as const;

const AC_TYPE_TO_FIELD: Record<string, (typeof AC_ALLOCATION_FIELD_NAMES)[number]> = {
  DEPARTMENT_ACCOUNTING: 'department_accounting_center',
  PRODUCTS_AND_SERVICES: 'product_and_services_accounting_center',
  PROJECTS: 'project_accounting_center',
  CONTRACTS: 'contract_accounting_center'
};

export interface WorkActivityAllocationSection {
  label: string;
  ids: unknown;
}

const normalizeDurationParts = (hours: number, minutes: number) => ({
  hours: Math.max(0, Math.floor(Number(hours) || 0)),
  minutes: Math.min(59, Math.max(0, Math.floor(Number(minutes) || 0)))
});

export const getNormalizedActivityDuration = (formValues: Record<string, unknown>) =>
  normalizeDurationParts(Number(formValues.duration_hours) || 0, Number(formValues.duration_minutes) || 0);

export const getTotalDurationMinutes = (formValues: Record<string, unknown>): number => {
  const normalized = getNormalizedActivityDuration(formValues);
  return normalized.hours * 60 + normalized.minutes;
};

const sumDurationForIds = (formValues: Record<string, unknown>, ids: unknown): number => {
  if (!Array.isArray(ids)) {
    return 0;
  }

  return ids.reduce((sum: number, id: string) => {
    const hours = Number(formValues[`${id}_hours`]) || 0;
    const minutes = Number(formValues[`${id}_minutes`]) || 0;
    const normalized = normalizeDurationParts(hours, minutes);
    return sum + normalized.hours * 60 + normalized.minutes;
  }, 0);
};

const buildAcAllocationItems = (formValues: Record<string, unknown>, ids: unknown): WorkActivityAcAllocationItem[] => {
  if (!Array.isArray(ids)) {
    return [];
  }

  return ids
    .filter((id): id is string => typeof id === 'string' && Boolean(id))
    .map((id) => {
      const normalized = normalizeDurationParts(
        Number(formValues[`${id}_hours`]) || 0,
        Number(formValues[`${id}_minutes`]) || 0
      );

      return {
        accounting_center_id: id,
        hours: normalized.hours,
        minutes: normalized.minutes
      };
    });
};

const buildBankAllocationItems = (
  formValues: Record<string, unknown>,
  ids: unknown
): WorkActivityBankAllocationItem[] => {
  if (!Array.isArray(ids)) {
    return [];
  }

  return ids
    .filter((id): id is string => typeof id === 'string' && Boolean(id))
    .map((id) => {
      const normalized = normalizeDurationParts(
        Number(formValues[`${id}_hours`]) || 0,
        Number(formValues[`${id}_minutes`]) || 0
      );

      return {
        bank_account_id: id,
        hours: normalized.hours,
        minutes: normalized.minutes
      };
    });
};

export const buildWorkActivityAllocations = (formValues: Record<string, unknown>): WorkActivityAcAllocationItem[] => {
  const allocations: WorkActivityAcAllocationItem[] = [];

  AC_ALLOCATION_FIELD_NAMES.forEach((fieldName) => {
    allocations.push(...buildAcAllocationItems(formValues, formValues[fieldName]));
  });

  return allocations;
};

export const buildWorkActivityBankAllocations = (
  formValues: Record<string, unknown>
): WorkActivityBankAllocationItem[] => buildBankAllocationItems(formValues, formValues.bank_account);

export const hasWorkActivityAllocations = (allocations: WorkActivityAcAllocationItem[]): boolean =>
  allocations.length > 0;

export const hasWorkActivityBankAllocations = (bankAllocations: WorkActivityBankAllocationItem[]): boolean =>
  bankAllocations.length > 0;

export interface WorkActivityCreateFormPayload {
  activity: string;
  description?: string;
  activity_date: string;
  duration_hours: number;
  duration_minutes: number;
  allocations: WorkActivityAcAllocationItem[];
  bank_allocations: WorkActivityBankAllocationItem[];
  documents?: File[];
  existing_documents?: string[];
}

export const buildWorkActivityCreateFormData = (payload: WorkActivityCreateFormPayload): FormData => {
  const formData = new FormData();

  formData.append('activity', payload.activity);
  formData.append('description', payload.description ?? '');
  formData.append('activity_date', payload.activity_date);
  formData.append('duration_hours', String(payload.duration_hours));
  formData.append('duration_minutes', String(payload.duration_minutes));

  payload.allocations.forEach((item, index) => {
    formData.append(`allocations[${index}][accounting_center_id]`, item.accounting_center_id);
    formData.append(`allocations[${index}][hours]`, String(item.hours));
    formData.append(`allocations[${index}][minutes]`, String(item.minutes));
  });

  payload.bank_allocations.forEach((item, index) => {
    formData.append(`bank_allocations[${index}][bank_account_id]`, item.bank_account_id);
    formData.append(`bank_allocations[${index}][hours]`, String(item.hours));
    formData.append(`bank_allocations[${index}][minutes]`, String(item.minutes));
  });

  if (payload.existing_documents) {
    formData.append('existing_documents', JSON.stringify(payload.existing_documents));
  }

  payload.documents?.forEach((file) => {
    formData.append('documents', file);
  });

  return formData;
};

export const mapWorkActivityToFormValues = (workActivity: WorkActivity): Record<string, unknown> => {
  const values: Record<string, unknown> = {
    activity_name: workActivity.activity ?? '',
    description: workActivity.description ?? '',
    activity_date: workActivity.activity_date ? new Date(workActivity.activity_date) : null,
    duration_hours: String(workActivity.duration_hours ?? ''),
    duration_minutes: String(workActivity.duration_minutes ?? ''),
    bank_account: [] as string[],
    department_accounting_center: [] as string[],
    product_and_services_accounting_center: [] as string[],
    project_accounting_center: [] as string[],
    contract_accounting_center: [] as string[]
  };

  const bankIds: string[] = [];
  (workActivity.bank_allocations ?? []).forEach((allocation) => {
    if (!allocation.bank_account_id) {
      return;
    }
    bankIds.push(allocation.bank_account_id);
    values[`${allocation.bank_account_id}_hours`] = String(allocation.hours ?? 0);
    values[`${allocation.bank_account_id}_minutes`] = String(allocation.minutes ?? 0);
  });
  values.bank_account = bankIds;

  const acIdsByField: Record<(typeof AC_ALLOCATION_FIELD_NAMES)[number], string[]> = {
    department_accounting_center: [],
    product_and_services_accounting_center: [],
    project_accounting_center: [],
    contract_accounting_center: []
  };

  (workActivity.allocations ?? []).forEach((allocation) => {
    if (!allocation.accounting_center_id) {
      return;
    }

    const fieldName = AC_TYPE_TO_FIELD[allocation.ac_type];
    if (!fieldName) {
      return;
    }

    acIdsByField[fieldName].push(allocation.accounting_center_id);
    values[`${allocation.accounting_center_id}_hours`] = String(allocation.hours ?? 0);
    values[`${allocation.accounting_center_id}_minutes`] = String(allocation.minutes ?? 0);
  });

  AC_ALLOCATION_FIELD_NAMES.forEach((fieldName) => {
    values[fieldName] = acIdsByField[fieldName];
  });

  return values;
};

export const getWorkActivityAllocationSections = (
  formValues: Record<string, unknown>
): WorkActivityAllocationSection[] =>
  [
    { label: 'Bank Account', ids: formValues.bank_account },
    { label: 'Department Accounting Center', ids: formValues.department_accounting_center },
    { label: 'Product and Services Accounting Center', ids: formValues.product_and_services_accounting_center },
    { label: 'Projects Accounting Center', ids: formValues.project_accounting_center },
    { label: 'Contract Accounting Center', ids: formValues.contract_accounting_center }
  ].filter((section) => Array.isArray(section.ids) && section.ids.length > 0);

export const validateWorkActivityAllocations = (
  formValues: Record<string, unknown>
): { isValid: boolean; errorMessage?: string } => {
  const totalDurationMinutes = getTotalDurationMinutes(formValues);
  const sectionsWithSelections = getWorkActivityAllocationSections(formValues);

  if (sectionsWithSelections.length === 0) {
    return {
      isValid: false,
      errorMessage: 'Please allocate this activity to at least one bank account or accounting center.'
    };
  }

  const mismatched = sectionsWithSelections.filter(
    (section) => sumDurationForIds(formValues, section.ids) !== totalDurationMinutes
  );

  if (mismatched.length > 0) {
    return {
      isValid: false,
      errorMessage: `Allocated duration must equal the logged activity duration for: ${mismatched.map((section) => section.label).join(', ')}`
    };
  }

  const acAllocations = buildWorkActivityAllocations(formValues);
  const bankAllocations = buildWorkActivityBankAllocations(formValues);

  if (!hasWorkActivityAllocations(acAllocations) && !hasWorkActivityBankAllocations(bankAllocations)) {
    return {
      isValid: false,
      errorMessage: 'Please allocate this activity to at least one bank account or accounting center.'
    };
  }

  return { isValid: true };
};

export const formatWorkActivityTableDuration = (hours: number, minutes: number): string => {
  const safeHours = Math.max(0, Math.floor(Number(hours) || 0));
  const safeMinutes = Math.min(59, Math.max(0, Math.floor(Number(minutes) || 0)));

  if (safeHours > 0 && safeMinutes > 0) {
    return `${safeHours}Hrs ${safeMinutes}Mins`;
  }

  if (safeHours > 0) {
    return `${safeHours}Hrs`;
  }

  if (safeMinutes > 0) {
    return `${safeMinutes}Mins`;
  }

  return '0Hrs';
};

export const formatRemainingAcohLabel = (acoh = 0, acohUsed = 0): string => {
  const remainingHours = Math.max(0, Number(acoh) - Number(acohUsed));
  const hours = Math.floor(remainingHours);
  const minutes = Math.round((remainingHours - hours) * 60);
  const normalizedHours = minutes === 60 ? hours + 1 : hours;
  const normalizedMinutes = minutes === 60 ? 0 : minutes;

  if (normalizedHours > 0 && normalizedMinutes > 0) {
    return `Remaining ACOH ${normalizedHours} Hrs ${normalizedMinutes} Min`;
  }

  if (normalizedHours > 0) {
    return `Remaining ACOH ${normalizedHours} Hrs`;
  }

  if (normalizedMinutes > 0) {
    return `Remaining ACOH ${normalizedMinutes} Min`;
  }

  return 'Remaining ACOH 0 Hrs';
};

export const getAccountingCenterMemberAcoh = (
  accountingCenter: { members?: { member_id: string; acoh?: number; acoh_used?: number }[] },
  memberId?: string
) => {
  const memberEntry = memberId ? accountingCenter.members?.find((member) => member.member_id === memberId) : undefined;

  return {
    acoh: memberEntry?.acoh ?? 0,
    acohUsed: memberEntry?.acoh_used ?? 0
  };
};

export const getWorkActivityAmount = (activity: {
  duration_hours: number;
  duration_minutes: number;
  allocations?: { hours: number; minutes: number; hourly_rate: number }[];
  bank_allocations?: { amount?: number }[];
}): number | null => {
  if (activity.allocations?.length) {
    return activity.allocations.reduce((sum, allocation) => {
      const totalHours = (Number(allocation.hours) || 0) + (Number(allocation.minutes) || 0) / 60;
      return sum + totalHours * (Number(allocation.hourly_rate) || 0);
    }, 0);
  }

  if (activity.bank_allocations?.length) {
    const bankTotal = activity.bank_allocations.reduce((sum, allocation) => sum + (Number(allocation.amount) || 0), 0);
    if (bankTotal > 0) {
      return bankTotal;
    }
  }

  return null;
};

export const formatWorkActivityTableAmount = (amount: number | null, currency = 'EUR'): string => {
  if (amount == null) {
    return '-';
  }

  const symbol = getCurrencySymbol(currency);
  const formattedAmount = Number.isInteger(amount) ? amount.toString() : amount.toFixed(1);
  return `${symbol}${formattedAmount}`;
};

export const getWorkActivityStatusLabel = (status: string): string => {
  switch (status) {
    case 'APPROVED':
      return 'Approved';
    case 'REJECTED':
      return 'Rejected';
    case 'PENDING':
    default:
      return 'Pending';
  }
};

export const formatWorkActivityDurationLabel = (hours: number, minutes: number): string => {
  const safeHours = Number.isFinite(hours) ? hours : 0;
  const safeMinutes = Number.isFinite(minutes) ? minutes : 0;

  if (safeHours > 0 && safeMinutes > 0) {
    return `${safeHours} Hour${safeHours === 1 ? '' : 's'} ${safeMinutes} Min`;
  }

  if (safeHours > 0) {
    return `${safeHours} Hour${safeHours === 1 ? '' : 's'}`;
  }

  if (safeMinutes > 0) {
    return `${safeMinutes} Min`;
  }

  return '0 Hours';
};

export const calculateWorkActivityCost = (hours: number, minutes: number, hourlyRate: number): number => {
  const totalHours = (Number(hours) || 0) + (Number(minutes) || 0) / 60;
  return totalHours * (Number(hourlyRate) || 0);
};

export const formatWorkActivityCost = (
  hours: number,
  minutes: number,
  hourlyRate: number,
  currency = 'EUR'
): string => {
  const amount = calculateWorkActivityCost(hours, minutes, hourlyRate);
  const symbol = getCurrencySymbol(currency);
  const formattedAmount = Number.isInteger(amount) ? amount.toString() : amount.toFixed(2);
  return `${symbol}${formattedAmount}`;
};
